import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import { Button, Icon, Modal, Table, TableHeaderCell, Loader } from "semantic-ui-react";
import { sortBy } from "lodash";
import { isRequestActive } from "redux-request-loading";

import { CustomMedicalType, MedicalTypeId } from "@teamprevent/common/healthSurveillance/models";
import Utils from "@teamprevent/common/utils";
import { toast } from "@teamprevent/common/toasts";
import { getMedicalTypeName } from "@teamprevent/common/customMedicalTypes/model";

import { clinicApi } from "../clinicApi";
import { HealthSurveillanceCaseInfoModel } from "../models";
import { caseTypeOptions } from "../../../healthSurveillance";
import { addOhCaseToMobileClinic } from "../actions";
import { ApplicationState } from "../../models";
import { ADD_RECALL_CASE_TO_MOBILE_CLINIC } from "../actionTypes";
import { useCustomMedicalTypes } from "../../../customMedicalTypes/hooks/useCustomMedicalTypes";

interface Props {
    clinicId: string;
    customerId: string;
    employeeRecordId: string;
    continueWithAction?: () => void;
}

type TickableHealthSurveillanceCaseInfoModel = HealthSurveillanceCaseInfoModel & { ticked?: boolean };

const OhCaseInformationModalRow = ({
    ohCase, customerId, employeeRecordId, onClick, customMedicalTypes
}: {
    ohCase: TickableHealthSurveillanceCaseInfoModel;
    customerId: string;
    employeeRecordId: string;
    onClick: (ohCase: TickableHealthSurveillanceCaseInfoModel, ticked: boolean) => void;
    customMedicalTypes: CustomMedicalType[];
}) => {
    const ohCaseMedicalTypeIds: MedicalTypeId[] = ohCase?.medicals;

    const medicals = React.useMemo((): string => {
        const medicalTypeNames = sortBy(ohCaseMedicalTypeIds.map(mt => getMedicalTypeName(customMedicalTypes, mt)), [], "name")
            .join(", ");

        return medicalTypeNames;
    }, [ohCaseMedicalTypeIds, customMedicalTypes]);

    const ticked = ohCase.ticked;
    const onTick = React.useCallback(() => onClick(ohCase, true), [ohCase, onClick]);
    const onUntick = React.useCallback(() => onClick(ohCase, false), [ohCase, onClick]);

    const actionButton = React.useMemo(() => {
        if (ticked) {
            return <Icon size="big" color="green" name="check circle" onClick={onUntick} />;
        }

        return <Icon size="big" color="grey" name="add circle" onClick={onTick} />;
    }, [ticked, onUntick, onTick]);

    return (<Table.Row key={ohCase.ohCaseId}>
        <Table.Cell>
            <a
                href={`/customers/${customerId}/employeeRecords/${employeeRecordId}/ohCases/${ohCase.ohCaseId}`}
                target="_blank"
                rel="noopener noreferrer"
            >
                {ohCase.caseId}
            </a>
        </Table.Cell>
        <Table.Cell>
            {caseTypeOptions.find(o => Utils.IsStringArraysContentEqual(o.values, ohCase.healthSurveillanceCaseTypes))?.text}
        </Table.Cell>
        <Table.Cell>{medicals}</Table.Cell>
        <Table.Cell>{ohCase.dueDate && ohCase.dueDate.format("L")}</Table.Cell>
        <Table.Cell>{actionButton}</Table.Cell>
    </Table.Row>);
};

export const OhCaseInformationModal: React.FunctionComponent<Props> = ({ clinicId, customerId, employeeRecordId, continueWithAction }: Props) => {
    const dispatch = useDispatch();

    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [recallCases, setRecalls] = React.useState<TickableHealthSurveillanceCaseInfoModel[]>([]);
    const anyRecallTicked = recallCases.findIndex(c => c.ticked) !== -1;
    const clinic = useSelector((state: ApplicationState) => state.clinic?.clinics?.find(c => c.id === clinicId));

    const { customMedicalTypes, isLoaded: areCustomMedicalTypesLoaded } = useCustomMedicalTypes({ enable: true });

    React.useEffect(() => {
        if (customerId && employeeRecordId) {
            (async () => {
                setLoading(true);
                const allRecalls = await clinicApi.getEmployeeRecordRecallCases(customerId, employeeRecordId, clinicId);
                const associatedCasesIds = clinic?.appointments?.map(a => a.ohCaseId);
                const notAssociatedCases = allRecalls.filter(c => !associatedCasesIds?.includes(c.ohCaseId));
                setRecalls(notAssociatedCases);
                setLoading(false);
            })();
        }
    }, [clinic, customerId, employeeRecordId, clinicId]);

    const onCaseInfoOpen = React.useCallback(() => setIsOpen(true), []);

    const onCancel = React.useCallback(() => {
        setIsOpen(false);
        setRecalls(prevValue => prevValue.map(recall => {
            return { ...recall, ticked: false };
        }));
    }, []);

    const onProceed = React.useCallback(async () => {
        const recallCaseIds = recallCases.filter(c => c.ticked).map(c => c.ohCaseId);
        if (recallCaseIds.length === 0) {
            toast.warning("No recall cases to add to the clinic!");
            return;
        }
        for (const recallCaseId of recallCaseIds) {
            await dispatch(addOhCaseToMobileClinic(customerId, clinicId, employeeRecordId, recallCaseId));
            setRecalls(prevState => prevState.filter(c => c.ohCaseId !== recallCaseId));
        }
        continueWithAction?.();
        setIsOpen(false);
    }, [recallCases, dispatch, customerId, clinicId, employeeRecordId, continueWithAction]);

    const ohCaseOnClick = React.useCallback((ohCase: TickableHealthSurveillanceCaseInfoModel, ticked: boolean) => {
        setRecalls(preValue => preValue.map(recall => {
            return recall.ohCaseId === ohCase.ohCaseId ? { ...recall, ticked } : recall;
        }));
    }, []);

    const isRecallCaseBeingAdded = useSelector((state: ApplicationState) => isRequestActive(state, ADD_RECALL_CASE_TO_MOBILE_CLINIC));

    return (
        <>
            <Modal open={isOpen} closeOnEscape={false} size="large">
                <Modal.Header>Case Information</Modal.Header>
                <Modal.Content scrolling>
                    <Table>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Case ID</Table.HeaderCell>
                                <Table.HeaderCell>Case Type</Table.HeaderCell>
                                <Table.HeaderCell width="6">Medicals</Table.HeaderCell>
                                <Table.HeaderCell>Due Date</Table.HeaderCell>
                                <TableHeaderCell />
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            <Loader active={isRecallCaseBeingAdded || !areCustomMedicalTypesLoaded} disabled={!isRecallCaseBeingAdded} />
                            {recallCases.map(ohCase => (<OhCaseInformationModalRow
                                key={ohCase.caseId}
                                customerId={customerId}
                                employeeRecordId={employeeRecordId}
                                ohCase={ohCase}
                                onClick={ohCaseOnClick}
                                customMedicalTypes={customMedicalTypes}
                            />))}
                        </Table.Body>
                    </Table>
                </Modal.Content>
                <Modal.Actions>
                    <Button content="Cancel" onClick={onCancel} />
                    <Button loading={isRecallCaseBeingAdded} content="Proceed" onClick={onProceed} disabled={!anyRecallTicked} />
                </Modal.Actions>
            </Modal>
            <Button content="Case Info" onClick={onCaseInfoOpen} loading={loading} />
        </>
    );
};
